import { DateTime } from 'luxon';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Link } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';

import globalTranslations from '../../../i18n/globalTranslations';
import Order from '../../../models/order/Order';
import { modelOf } from '../../../prop-types';
import RouteService from '../../../services/RouteService';
import AccountStore from '../../../store/AccountStore';
import CartStore from '../../../store/CartStore';
import OrderTotalClass from '../../../types/OrderTotalClass';
import Paths from '../../../types/Paths';
import ProductTypeClass from '../../../types/ProductTypeClass';
import RequestState from '../../../types/RequestState';
import generatePath from '../../../util/generatePath';
import Price from '../../product/Price';
import OrderCloneButton from '../OrderCloneButton';
import OrderLink from '../OrderLink';
import OrderStatusLabel from '../OrderStatusLabel';

const OrderPreview = ({ accountStore, cartStore, routeService, order }) => {
  const getTotalPrice = () => {
    const totalPrice =
      order.totals.length &&
      order.totals.find((total) => total.class === OrderTotalClass.TOTAL);

    return totalPrice.value || null;
  };

  const getTotalTaxlessPrice = () => {
    const totalPrice = order.totals.find(
      (total) => total.class === OrderTotalClass.TAX_FREE_TOTAL
    );

    return totalPrice.value || null;
  };

  const getOrderStatus = () => {
    if (!order.last_status) {
      return null;
    }

    return <OrderStatusLabel status={order.last_status} />;
  };

  const getProductCount = () => {
    const physicalProducts = getPhysicalProducts();
    if (!physicalProducts) {
      return null;
    }

    return (
      <FormattedMessage
        {...globalTranslations.productCount}
        values={{ count: physicalProducts.length }}
      />
    );
  };

  const getOrderReference = () => {
    const reference = order.getOrderReference();

    return (
      reference && (
        <FormattedMessage
          {...globalTranslations.yourReferenceSentence}
          values={{ reference }}
        />
      )
    );
  };

  const getPhysicalProducts = () => {
    return order.products.filter(
      (orderProduct) => orderProduct.type === ProductTypeClass.PHYSICAL
    );
  };

  const getPath = () => {
    return routeService.getPath(
      generatePath(Paths.MyAccountOrder, { id: order.id })
    );
  };

  const getOrderViewButton = () => {
    return (
      <Link to={getPath()}>
        <Button className="OrderPreview__button-view" color="primary">
          <FormattedMessage {...globalTranslations.toOrder} />
        </Button>
      </Link>
    );
  };

  const getOrderDate = () => {
    return DateTime.fromISO(order.date_purchased).toLocaleString();
  };

  const shippingInfo = order.getCustomerShippingInfo();

  return (
    <div className="OrderPreview">
      <Row className="OrderPreview__row">
        <Col className="OrderPreview__order-information">
          <div className="OrderPreview__order-number">
            <span className="OrderPreview__link">
              <OrderLink id={order.id} link={getPath()} />
            </span>
          </div>
          <div className="OrderPreview__order-date">
            <FormattedMessage
              id="myAccount.orderDate"
              defaultMessage="Ordered on: {orderDate}"
              values={{
                orderDate: getOrderDate(),
              }}
            />
          </div>
          <div className="OrderPreview__customer">
            <div className="OrderPreview__customer-name">
              {shippingInfo.name}
            </div>
            <div className="OrderPreview__customer-address">
              {shippingInfo.street_address}
            </div>
            <div className="OrderPreview__customer-area">
              <span className="OrderPreview__customer-zip">
                {shippingInfo.postalcode}
              </span>
              <span className="OrderPreview__customer-city">
                {shippingInfo.city}
              </span>
            </div>
          </div>
          <div className="OrderPreview__count">{getProductCount()}</div>
          <div className="OrderPreview__order-reference">
            {getOrderReference()}
          </div>
          <div className="OrderPreview__status">{getOrderStatus()}</div>
        </Col>
        <Col xs="auto" className="OrderPreview__order-checkout">
          <div className="OrderPreview__buttons">
            {getOrderViewButton()}
            <OrderCloneButton
              order={order}
              disabled={!cartStore.state === RequestState.LOADING}
            />
          </div>
          <div className="OrderPreview__price">
            <span className="OrderPreview__total-sentence">
              <FormattedMessage {...globalTranslations.totalPriceSentence} />
              {':'}
            </span>
            <Price price={getTotalPrice()} />
            {accountStore.isRetailer && (
              <>
                <Price
                  price={getTotalTaxlessPrice()}
                  suffix={
                    <FormattedMessage {...globalTranslations.taxExcluded} />
                  }
                />
              </>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

OrderPreview.propTypes = {
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  accountStore: modelOf(AccountStore).isRequired,
  cartStore: modelOf(CartStore).isRequired,
  order: modelOf(Order),
  intl: intlShape.isRequired,
};

export default inject(
  'routeService',
  'accountStore',
  'cartStore'
)(injectIntl(observer(OrderPreview)));
